/**
 * Simple utils, some of which are copied from Portal's codebase
 * for modal functionalityw.
 * Portal has a TODO: to migrate debounce to OmniUI
 * debounce below will need removed when that's available
 * @namespace utils
 */

import { MONTH_NAMES } from './constants.js';

/**
 * Host-based environment detection copied from Portal's login.html
 * @function
 * @memberof utils
 * @returns {'prod'|'qa'|'stg'|'local'} - A normalized environment key
 */
export const getEnvironment = () => {
  const envRegex = /^(dev|qa|stg|localhost|127\.0\.0\.1|0\.0\.0\.0)/;
  const environment = window.location.hostname.match(envRegex);
  if (environment)
    return environment[1] === 'localhost' ? 'local' : environment[1];
  return 'prod';
};

/**
 * Utility function to add a cookie with a particular name/value/expiration
 * @function
 * @memberof utils
 * @param {string} name - The cookie's name
 * @param {string} value - The cookie's value
 * @param {number} hours - The number of hours in the future to expire the cookie
 * @param {string} path - The path associated with the cookie
 */
export const setCookie = (name, value, hours = 12, path = '/') => {
  const expires = new Date(Date.now() + hours * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires}; path=${path}`;
};

/**
 * Returns a cookie value given its name
 * @function
 * @memberof utils
 * @param {string} name - The cookie's name
 * @returns {string}
 */
export const getCookie = name =>
  document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');

/**
 * Returns a cookie value given its name
 * @function
 * @memberof utils
 * @param {string} name - The cookie's name
 * @param {string} path - The path associated with the cookie
 */
export const deleteCookie = (name, path) => {
  setCookie(name, '', -1, path);
};

/**
 * A helper to return an ordinal number by appending 'th', 'st', 'nd',
 * or 'rd' to it, from any gvien number.
 * - This was inspired by shopify's publiclyavailable snippet.
 * @function
 * @memberof urils
 * @param {number} - The number to make ordinal.
 * @returns {string} - The ordinal number
 */
export const getOrdinalNumber = num => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;

  return `${num}${
    // use remainder of %10 to get lower indices
    s[(v - 20) % 10] ||
    // indexes 2/3 make 2nd/3rd
    s[v] ||
    // default to th
    s[0]
  }`;
};

export const getSpokenDate = (d = '') => {
  const splitJoinChar = '-';
  const components = d.split('-');
  const date = new Date(
    components[0]?.length === 4
      ? `${components[1]}-${components[2]}-${components[0]}`
      : components.join(splitJoinChar)
  );

  if (!(date instanceof Date) || Number.isNaN(Number(date))) return 'N/A';

  return `${MONTH_NAMES[date.getMonth()]} ${getOrdinalNumber(date.getDate())}`;
};

// Turn any string into title case (all words begin with a capital letter
export const toTitleCase = (str = '') =>
  str.replace(
    /\w\S*/g,
    // don't lowercase the rest of the title if the first is already capitalzied in case of acronyms
    word =>
      word.charAt(0).toUpperCase() === word.charAt(0)
        ? word
        : `${word.charAt(0).toUpperCase()}${word.substr(1).toLowerCase()}`
  );

/**
 * Formats any large number (up to 1,999,999,999) as a float with an abbreviated scale indicator
 */
export const formatLargeNumber = (num, precision = 2) => {
  if (num >= 1_000_000_000)
    return `${(num / 1_000_000_000).toFixed(precision)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(precision)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(precision)}K`;

  return num;
};

/**
 * Takes any string and replaces placeholders (i.e. {{someValue}}) with the value from the object passed in.
 * Replacement string start and end can be customized (i.e. <% %>) but must directly surround the variable name (no spaces surrounding it).
 * SO - https://stackoverflow.com/a/70492071
 */
export const compileTemplate = (text, obj, start = '{{', end = '}}') => {
  const rgx = new RegExp(`${start}(.+?)${end}`, 'g');
  const replacer = (_, part) =>
    part.split('.').reduce((o, k) => (o || {})[k], obj);

  return text.replace(rgx, replacer);
};

// Takes a truthy value and returns it with HTTP if doesn't already have
export const useHTTP = str => {
  if (!str) return str;

  const prefix = /^https?:\/\//;
  if (prefix.test(str)) {
    return str;
  }
  return `http://${str}`;
};
